import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory} from 'src/rerouter';
import {useAsyncDependency} from 'src/hooks/useAsyncDependency';
import type {
  ReferralAgencyData,
  ReferralLeaderBoard,
} from 'src/types/referral-v2';
import type {
  GetReferralTrackingApiResponse,
  RecentActivity,
  UserDetails as UserDetailsData,
  ReferralLinks,
} from 'src/types/referrer-portal';
import {DEFAULT_TABLE_PAGE_SIZE} from 'src/utils/referral-v2';
import {getAllReferralsDetails} from 'src/action-creators/referrer-portal/tracking';
import {
  selectSocialLinks,
  selectReferralRequest,
  selectUserDetails,
} from 'src/selectors/referrer-portal';
import {AnalyticsService} from 'src/analytics';

import {
  getDateRangeOfDaysDifference,
  getTableBorderRadius,
  getFormattedDate,
  isReferrerPortal,
} from 'src/components/referral-v2/common';
import classify from 'src/utils/classify';

import {
  BodySmall,
  SubTitleMedium,
  FormLabelMedium,
  FormLabelSmall,
  BodyMedium,
  SubTitleSmall,
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  EmptyState,
  TableTopBar,
  Table,
  Avatar,
  Link,
  Icon,
  Panel,
  PanelBody,
  BasicSingleCell,
  Truncate,
} from '@spaced-out/ui-design-system/lib/components/index';
import SocialShare from 'src/components/referral-v2/configure/social-share';

import css from 'src/components/referrer-portal/dashboard/dashboard.css';


export const ReferralRequest = ({
  className,
  appliedDateFilter,
}: {
  className?: string;
  appliedDateFilter: string;
}) => {
  const dispatch = useDispatch();
  const {t, language} = useLabelTranslation();

  // @ts-expect-error - TS2322 - Type 'import("/Users/diwaker/SpacedOut/ui-server/src/types/referral-v2").GetReferralTrackingApiResponse' is not assignable to type 'import("/Users/diwaker/SpacedOut/ui-server/src/types/referrer-portal").GetReferralTrackingApiResponse'.
  const referralRequest: GetReferralTrackingApiResponse | null | undefined =
    useSelector(selectReferralRequest);
  const router = useHistory();
  const isReferrerPortalEnv = isReferrerPortal();

  const leadRequestPageUrl = isReferrerPortalEnv
    ? '/referrals/leads'
    : '/referralv2/tracking/leads';

  const headers = [
    {label: t('Referral_Name', 'Referral Name'), key: 'name'},
    {
      label: t('Referred_on', 'Referred on'),
      key: 'requested_on',
      // @ts-expect-error - TS7031 - Binding element 'requested_on' implicitly has an 'any' type.
      render: ({data: {requested_on}}) => (
        <BasicSingleCell>
          <BodyMedium>
            {requested_on ? getFormattedDate(requested_on, language) : '-'}
          </BodyMedium>
        </BasicSingleCell>
      ),
    },
    {
      label: t('Source', 'Source'),
      key: 'source',
      // @ts-expect-error - TS7031 - Binding element 'utm_medium' implicitly has an 'any' type. | TS7031 - Binding element 'utm_source' implicitly has an 'any' type.
      render: ({data: {utm_medium, utm_source}}) => (
        <BasicSingleCell>
          <BodyMedium>
            {/* TODO[Arush]: Check what is the expected format for this */}
            {utm_medium
              ? `${utm_medium} ${utm_source ? ` (${utm_source})` : ''}`
              : '-'}
          </BodyMedium>
        </BasicSingleCell>
      ),
    },
  ];

  const query = getDateRangeOfDaysDifference(appliedDateFilter);
  const filtersPayload = {
    filters: [
      {
        key: 'timeframe',
        values: query,
      },
    ],
    limit: DEFAULT_TABLE_PAGE_SIZE,
    offset: 0,
  } as const;

  const isDataLoading = useAsyncDependency(
    // @ts-expect-error - TS2322 - Type 'ThunkAction<void>' is not assignable to type 'Promise<unknown>'. | TS2345 - Argument of type '{ readonly filters: readonly [{ readonly key: "timeframe"; readonly values: { end_date: string; start_date: string; }; }]; readonly limit: 13; readonly offset: 0; }' is not assignable to parameter of type 'ReferralTrackingPayload'.
    () => dispatch(getAllReferralsDetails(filtersPayload, true)),
    [],
  );

  return (
    <div className={classify(css.requestTableContainer, className)}>
      <TableTopBar>
        <SubTitleMedium>
          {t('Referral_Request', 'Referral Request')}
        </SubTitleMedium>
        {referralRequest && referralRequest.tracking.length > 10 && (
          <Link
            as="buttonTextMedium"
            onClick={() => router.push(leadRequestPageUrl)}
            underline={false}
            color="primary"
          >
            {t('View_All', 'View All')}
          </Link>
        )}
      </TableTopBar>
      <Table
        // @ts-expect-error - TS2322 - Type '({ label: string; key: string; render?: undefined; } | { label: string; key: string; render: ({ data: { requested_on } }: { data: { requested_on: any; }; }) => Element; } | { label: string; key: string; render: ({ data: { utm_medium, utm_source } }: { data: { ...; }; }) => Element; })[]' is not assignable to type 'GenericHeaderItems<ReferralTrackingDetails, GenericObject>'.
        headers={headers}
        entries={referralRequest ? referralRequest.tracking : []}
        classNames={{wrapper: css.table, tableBody: css.tableBody}}
        isLoading={isDataLoading}
        borderRadius={getTableBorderRadius(0)}
        emptyText={
          <EmptyState
            title={t('No_result_found', 'No result found')}
            description={`${t(
              'When_candidates_make_requests_through_social_media_the_list_will_be_displayed_here',
              'When candidates make requests through social media, the list will be displayed here',
            )}.`}
            imageVariant="data"
            classNames={{description: css.emptyText}}
          />
        }
        stickyHeader={true}
        idName={'request_id'}
      />
    </div>
  );
};

export const ReferralLeaderBoardComponent = () => {
  const {t} = useLabelTranslation();
  const referralLeaderboard: ReferralLeaderBoard[] | null | undefined =
    useSelector((state) => state.referrerPortal.referral_leaderboard);

  const headers = [
    {label: t('Name', 'Name'), key: 'name'},
    {label: t('Referrals', 'Referrals'), key: 'referrals'},
    {label: t('Hired', 'Hired'), key: 'hires'},
  ];
  return (
    <div className={css.tableContainer}>
      <TableTopBar>
        <SubTitleMedium>{t('Leaderboard', 'Leaderboard')}</SubTitleMedium>
      </TableTopBar>
      <Table
        // @ts-expect-error - TS2322 - Type '{ label: string; key: string; }[]' is not assignable to type 'GenericHeaderItems<ReferralLeaderBoard, GenericObject>'.
        headers={headers}
        borderRadius={getTableBorderRadius(0)}
        entries={referralLeaderboard ? referralLeaderboard : []}
        classNames={{
          wrapper: css.table,
        }}
        emptyText={
          <EmptyState
            title={t('No_result_found', 'No result found')}
            description={`${t(
              'To_be_part_of_the_referral_leaderboard_you_need_to_start_referring_more',
              'To be part of the referral leaderboard you need to start referring more',
            )}.`}
            imageVariant="data"
          />
        }
        stickyHeader={true}
        idName={'id'}
      />
    </div>
  );
};

export const OverAllStatusInfo = ({
  overAllImpactInfo,
  showCurrency = false,
}: {
  overAllImpactInfo: ReferralAgencyData | null | undefined;
  showCurrency?: boolean;
}) => {
  const {t} = useLabelTranslation();
  if (!overAllImpactInfo) {
    return (
      <div className={css.cardMiddleContent}>
        <BodySmall>{t('No_status_found', 'No status found')}</BodySmall>
      </div>
    );
  }
  const {amount, percentage_change, change_type} = overAllImpactInfo;
  return (
    <div className={css.cardMiddleContent}>
      {amount !== null && (
        <div>
          {showCurrency && '$'}
          {amount}
        </div>
      )}
      <div>
        {change_type && change_type !== 'no_change' && (
          <Icon
            size="small"
            name={change_type === 'increase' ? 'arrow-up' : 'arrow-down'}
            color={change_type === 'increase' ? 'success' : 'danger'}
          />
        )}
        {!!percentage_change && (
          <FormLabelMedium>{percentage_change}%</FormLabelMedium>
        )}
      </div>
    </div>
  );
};

export const UserDetails = ({
  profileUrl,
}: {
  profileUrl: string | null | undefined;
}) => {
  const {t} = useLabelTranslation();
  const router = useHistory();

  const [showForm, setShowForm] = React.useState(false);

  const userData: UserDetailsData | null | undefined =
    useSelector(selectUserDetails);

  const socialLinks: ReferralLinks | null | undefined =
    useSelector(selectSocialLinks);

  const isReferrerPortalEnv = isReferrerPortal();

  const editProfileUrl = isReferrerPortalEnv
    ? '/settings/profile'
    : '/referralv2/settings';

  const editPaymentUrl = isReferrerPortalEnv
    ? '/earnings/payments'
    : '/referralv2/earnings/payments';
  const messagingLink = socialLinks ? socialLinks.user_link : '';

  const generateLinkWithSource = (medium: string, source: string): string => {
    return socialLinks
      ? `${socialLinks.long_url}?utm_medium=${medium}&utm_source=${source}`
      : '';
  };

  const onTrackAnalytics = (social: string, link: string) => {
    AnalyticsService.track(social, {
      userPersonalLink: link,
    });
  };

  React.useEffect(() => {
    const handlePostMessage = (event: any) => {
      if (event?.data?.type === 'CLOSE_REFER_NOW_PANEL') {
        setShowForm(event.data.showReferNowPanel);
      }
    };
    window.addEventListener('message', handlePostMessage);
    return () => {
      window.removeEventListener('message', handlePostMessage);
    };
  }, []);

  return (
    <Card>
      <CardContent className={css.userDetailsContainer}>
        <div className={css.userContent}>
          <Avatar
            size="large"
            imageSrc={profileUrl || ''}
            text={userData?.name}
          />
          <div className={css.userDetails}>
            <Truncate>
              <SubTitleMedium>{userData?.name}</SubTitleMedium>
            </Truncate>
            <BodySmall>{userData?.location}</BodySmall>
            <div className={css.userCardAction}>
              <Link
                onClick={() => router.push(editProfileUrl)}
                color="clickable"
              >
                {t('Edit_Profile', 'Edit Profile')}
              </Link>
              |
              <Link
                onClick={() => router.push(editPaymentUrl)}
                color="clickable"
              >
                {t('Edit_Payment', 'Edit Payment')}
              </Link>
            </div>
          </div>
        </div>
        <div className={css.activity}>
          <FormLabelSmall>{t('Share_Form', 'Share Form')}</FormLabelSmall>
          <SocialShare
            facebookLink={generateLinkWithSource('social', 'facebook')}
            linkedinLink={generateLinkWithSource('social', 'linkedin')}
            twitterLink={generateLinkWithSource('social', 'twitter')}
            emailLink={generateLinkWithSource('email', '')}
            copyLinkText={generateLinkWithSource('direct', '')}
            messagingLink={messagingLink}
            onTrackAnalytics={onTrackAnalytics}
          />
        </div>
      </CardContent>
      {/* @ts-expect-error - TS2322 - Type '{ children: Element; className: string; onClick: () => void; }' is not assignable to type 'IntrinsicAttributes & CardChildProps'. */}
      <CardFooter className={css.formLinkBtn} onClick={() => setShowForm(true)}>
        <Link
          as="buttonTextMedium"
          iconRightName="chevron-right"
          color="clickable"
        >
          {t(
            'View_personalised_referral_form',
            'View personalised referral form',
          )}
        </Link>
      </CardFooter>
      <Panel
        isOpen={showForm}
        tapOutsideToClose
        onClose={() => setShowForm(false)}
        anchor="right"
        size="large"
        hideBackdrop={false}
      >
        {socialLinks?.long_url ? (
          <iframe
            src={`${socialLinks.long_url}?iframe=true`}
            width="100%"
            height="100%"
            frameBorder="0"
          />
        ) : (
          <PanelBody>
            <EmptyState
              title={t(
                'Personalized_referral_form_link_not_available',
                'Personalized referral form link not available',
              )}
              imageVariant="data"
            />
          </PanelBody>
        )}
      </Panel>
    </Card>
  );
};

export const ReferralActivity = () => {
  const {t, language} = useLabelTranslation();
  const activityLogs: RecentActivity[] | null | undefined = useSelector(
    (state) => state.referrerPortal.referral_activities,
  );

  return (
    <Card classNames={{wrapper: css.recentActivityCard}}>
      <CardHeader>{t('Recent_Activities', 'Recent Activities')}</CardHeader>
      <CardContent className={css.activityContent}>
        {!activityLogs || activityLogs.length === 0 ? (
          <EmptyState
            classNames={{wrapper: css.emptyState}}
            imageVariant="data"
            title={`${t(
              'No_recent_activity_found',
              'No recent activity found',
            )}.`}
          />
        ) : (
          activityLogs.map((activity, index) => {
            const isLastElement = activityLogs.length - 1 === index;
            return (
              <>
                {activity.activity_type === 'Payment Log' ? (
                  <BodySmall className={css.activity} key={activity.amount}>
                    {activity.candidate}
                    {` ${t('was', 'was')}`}
                    <SubTitleSmall>
                      {`${t('hired_as_a', 'hired as a')} `}
                      {activity.job_title}
                    </SubTitleSmall>
                    {`${t('and', 'and')} `}
                    <SubTitleSmall>${activity.amount}</SubTitleSmall>{' '}
                    {`${t('Credited_on', 'Credited on')} `}
                    {getFormattedDate(activity.payment_date, language)}
                  </BodySmall>
                ) : (
                  <BodySmall className={css.activity} key={activity.candidate}>
                    {activity.candidate}
                    {` ${t('was_moved_from', 'was moved from')}`}{' '}
                    <SubTitleSmall>{activity.from_status}</SubTitleSmall>
                    {` ${t('to', 'to')}`}{' '}
                    <SubTitleSmall>{activity.to_status}</SubTitleSmall>
                    {` ${t('on', 'on')}`}{' '}
                    {getFormattedDate(activity.time_created, language)}
                  </BodySmall>
                )}
                {!isLastElement && <div className={css.dividerLine} />}
              </>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};
